import {
  AUDIO_POLICY_ALWAYS_MUTE,
  AUDIO_POLICY_ON_INTERACTION,
} from "../../src/js/constants";

// NOTE: This is converted from video-a (renamed because video-b is not being converted).

const { Component, DOMUtils, Utils, Logger, LiftoffMRAID } = window.Liftoff;

const HTML = `
  <div class="video">
    <canvas class="video-copy"></canvas>
    <div class="video-letterbox"></div>
    <video preload="auto" webkit-playsinline playsinline>
      <source class="mp4" type="video/mp4"></source>
    </video>
    <div class="video-progress">
      <div class="video-progress-bar"></div>
    </div>
  </div>
`;
// A 1x1 pixel transparent GIF.
const TRANSPARENT_GIF =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

Component.register(
  "video",
  class Video extends Component {

    static unmute() {
      const videos = document.getElementsByTagName("video");
      for (let video of videos) {
        if (!video.dataset.alwaysMute && video.muted) {
          video.muted = false;
        }
      }
    }

    constructor(config) {
      super(config);

      Utils.assignWithDefaults(
        this,
        {
          mp4: null,
          alwaysMute: false,
          vpos: "top",
          loop: true,
          letterboxColor: "transparent",
          letterboxColorFill: null,
          blurredLetterbox: false,
          blurAmount: 10,
          showProgress: false,
        },
        this.config
      );

      this.videoCanPlay = false;
      this.videoMetaLoaded = false;
    }

    load() {
      this.el = DOMUtils.strToElement(HTML);
      this.el.classList.toggle("show-progress", this.showProgress);
      this.timeAtLoad = new Date();

      this.liftoffEl = document.getElementById("liftoff-layout");
      this.videoEl = this.el.querySelector("video");
      this.canvasEl = this.el.querySelector("canvas");
      this.letterboxEl = this.el.querySelector(".video-letterbox");
      this.canvasCtx = this.canvasEl.getContext("2d")
      this.videoEl.loop = this.loop;
      const mp4SourceEl = this.videoEl.querySelector(".mp4");
      if (this.mp4 == null) {
        mp4SourceEl.remove();
      } else {
        mp4SourceEl.src = this.mp4;
      }

      this.videoEl.setAttribute("poster", TRANSPARENT_GIF);
      const globalAudioPolicy = Liftoff.env.creative_policy.audio_policy;
      const alwaysMute = this.alwaysMute || globalAudioPolicy === AUDIO_POLICY_ALWAYS_MUTE;
      this.videoEl.muted = alwaysMute || globalAudioPolicy === AUDIO_POLICY_ON_INTERACTION;
      if (alwaysMute) {
        this.videoEl.dataset.alwaysMute = alwaysMute;
      }
      this.addEventListeners(globalAudioPolicy !== AUDIO_POLICY_ALWAYS_MUTE);

      if (this.blurredLetterbox) {
        this.videoEl.addEventListener("play", this.updateVideoBlur.bind(this));
      }

      this.videoEl.load();
      return this.metaDataLoadedPromise;
    }

    layout(parentNode) {
      super.layout(parentNode);
      this.parentNode.appendChild(this.el);
      this.resize();
    }

    // It's necessary to explicitly size the height of the video on iOS, because
    // otherwise, the video element fills the height of the container, getting
    // letterboxed and vertically centered within the container.
    resize() {
      const aspect = this.videoEl.videoWidth / this.videoEl.videoHeight;
      const newHeight = this.el.getBoundingClientRect().width / aspect;
      this.videoEl.style.height = newHeight + "px";
      this.el.classList.add(`vpos-${this.vpos}`);
      this.letterboxEl.style.background = this.letterboxColorFill ? this.letterboxColorFill : this.letterboxColor;

      if (this.blurredLetterbox) {
        this.canvasEl.width = 400;
        this.canvasEl.height = this.canvasEl.width / aspect;
        this.letterboxEl.style["-webkit-backdrop-filter"] = `blur(${this.blurAmount}px)`;
        this.letterboxEl.style.backdropFilter = `blur(${this.blurAmount}px)`;
      } else {
        this.letterboxEl.style["-webkit-backdrop-filter"] = null;
        this.letterboxEl.style.backdropFilter = null;
      }
      return true;
    }

    start() {
      this.started = true;
      this.playIfReady();
    }

    stop() {
      this.videoEl.pause();
    }

    restart() {
      this.stop();
      this.videoEl.currentTime = 0;
      this.start();
    }

    addEventListeners(unmute) {
      DOMUtils.addOneTimeListener(this.videoEl, "canplay", () => {
        Logger.recordMetricV2(
          "video_canplay_ms",
          Date.now() - LIFTOFF_START_TS
        );
      });
      DOMUtils.addOneTimeListener(this.videoEl, "canplaythrough", () => {
        Logger.recordMetricV2(
          "video_canplaythrough_ms",
          Date.now() - LIFTOFF_START_TS
        );
        this.videoCanPlay = true;
        this.playIfReady();
      });
      DOMUtils.addOneTimeListener(this.videoEl, "timeupdate", () => {
        this.videoEl.style.visibility = "visible";
      });
      if (unmute) {
        if (!Video.unmuteAdded) {
          Video.unmuteAdded = true;
          this.liftoffEl.addEventListener("mousedown", Video.unmute);
          this.liftoffEl.addEventListener("mouseup", Video.unmute);
        }
      }
      this.videoEl.addEventListener("ended", () => this.emit("videoComplete"));
      this.metaDataLoadedPromise = new Promise((resolve) => {
        this.videoEl.addEventListener("loadedmetadata", () => {
          this.videoMetaLoaded = true;

          resolve();
        });
      });
      // We play this video on load. If mraid indicates that the ad has been
      // closed (hidden), then pause. Continue playback when the ad comes back
      // into view.
      LiftoffMRAID.onViewableChange((viewable) => {
        try {
          if (viewable) {
            this.playIfReady();
          } else {
            this.videoEl.pause();
          }
        } catch (e) {
          this.error(e);
        }
      });
    }

    onProgressUpdate() {
      if (!(this.started && this.videoMetaLoaded)) return;

      const progress = this.videoEl.currentTime / this.videoEl.duration;
      this.el.querySelector(".video-progress-bar").style.width = `${
        progress * 100
      }%`;

      if (!this.videoEl.paused) {
        requestAnimationFrame(this.onProgressUpdate.bind(this));
      }
    }

    // Renders and plays an MP4 video.
    playIfReady() {
      const play = () => {
        const promise = this.videoEl.play();
        this.onProgressUpdate();

        return promise;
      };

      if (this.videoCanPlay && this.started) {
        // `HTMLMediaElement.play()` returns a Promise in some browsers, but
        // support is poor. If a promise was returned, use it to try to re-play
        // the video on error. Otherwise, check after a fixed time if a video is
        // playing and try re-playing if necessary.
        const promise = play();
        if (promise != null && Utils.type(promise.catch) === "function") {
          promise.catch(play);
        } else {
          setTimeout(() => {
            if (this.videoEl.paused) {
              play();
            }
          }, 500);
        }
      }
    }

    updateVideoBlur() {
      if (this.videoEl.paused || this.videoEl.ended) return;
      this.canvasCtx.drawImage(
        this.videoEl,
        0,
        0,
        this.canvasEl.width,
        this.canvasEl.height
      );
      requestAnimationFrame(this.updateVideoBlur.bind(this));
    }

  }
);



// WEBPACK FOOTER //
// ./components/video/video.js